/**
 * Community recipes API (Camp Essentials > Recipes).
 */
import api from '../../../utils/api';
import { buildMainImage, buildThumbnail } from '../utils/listingPhotoUtils';

const BASE = '/community-recipes/';

export const communityRecipesService = {
  list(params = {}) {
    return api.get(BASE, { params }).then((res) => res.data);
  },

  listFavorites() {
    return api.get(BASE, { params: { favorites: 1 } }).then((res) => res.data);
  },

  feed({ limit = 8, offset = 0 } = {}) {
    return api
      .get(`${BASE}feed/`, { params: { limit, offset } })
      .then((res) => res.data);
  },

  helpMeCook(ingredientsText) {
    return api
      .post(`${BASE}help-me-cook/`, { ingredients_text: ingredientsText })
      .then((res) => res.data);
  },

  get(id) {
    return api.get(`${BASE}${id}/`).then((res) => res.data);
  },

  create(payload) {
    return api.post(BASE, payload).then((res) => res.data);
  },

  update(id, payload) {
    return api.patch(`${BASE}${id}/`, payload).then((res) => res.data);
  },

  delete(id) {
    return api.delete(`${BASE}${id}/`);
  },

  addPhoto(recipeId, payload) {
    return api.post(`${BASE}${recipeId}/add-photo/`, payload).then((res) => res.data);
  },

  initRecipePhotoUpload(recipeId, filename, contentType = 'image/jpeg') {
    return api
      .post('/assets/init/', {
        scope: 'recipe-photo',
        recipe_id: recipeId,
        filename: filename || 'photo.jpg',
        content_type: contentType,
      })
      .then((res) => res.data);
  },

  async uploadRecipePhoto(recipeId, file) {
    const contentType = file.type || 'image/jpeg';
    const init = await this.initRecipePhotoUpload(recipeId, file.name, contentType);
    if (!init || !init.upload_url || !init.object_key) {
      throw new Error('Failed to get upload URL');
    }
    const [main, thumb] = await Promise.all([buildMainImage(file), buildThumbnail(file)]);
    if (main.blob.size > 10 * 1024 * 1024) {
      throw new Error('Image is too large (max 10 MB after resize)');
    }
    await fetch(init.upload_url, {
      method: 'PUT',
      body: main.blob,
      headers: { 'Content-Type': contentType },
    });
    if (init.thumb_upload_url && init.thumb_object_key) {
      await fetch(init.thumb_upload_url, {
        method: 'PUT',
        body: thumb.blob,
        headers: { 'Content-Type': contentType },
      });
    }
    return this.addPhoto(recipeId, {
      object_key: init.object_key,
      thumb_object_key: init.thumb_object_key || undefined,
      position: 1,
      width: main.width,
      height: main.height,
      size_bytes: main.blob.size,
    });
  },

  setFavorite(recipeId, favorited) {
    if (favorited) {
      return api.post(`${BASE}${recipeId}/favorite/`).then((res) => res.data);
    }
    return api.delete(`${BASE}${recipeId}/favorite/`).then((res) => res.data);
  },

  setRating(recipeId, stars) {
    return api.put(`${BASE}${recipeId}/rating/`, { stars }).then((res) => res.data);
  },

  report(recipeId, category, message = '') {
    return api
      .post(`${BASE}${recipeId}/report/`, { category, message })
      .then((res) => res.data);
  },
};
